'use client';

import type { ReactNode } from 'react';
import type { Locale as DateFnsLocale } from 'date-fns';
import type { Theme, Components } from '@mui/material/styles';

import { es as dateFnsEs, enUS as dateFnsEnUS } from 'date-fns/locale';
import { useAuthControllerUpdateProfile } from '@/apis/backend/generated/auth/auth';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { esES as muiEsES, enUS as muiEnUS } from '@mui/material/locale';

import { useAuthStore } from 'src/lib/store/auth-store';
import i18n, {
  FALLBACK_LANGUAGE,
  SUPPORTED_LANGUAGES,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from 'src/locales/i18n';

// ----------------------------------------------------------------------

type MuiLocaleBundle = { components?: Components<Theme> };

interface LocaleContextValue {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  /** Active date-fns locale for `LocalizationProvider` / formatters. */
  dateFnsLocale: DateFnsLocale;
  /** Active `@mui/material/locale` bundle for `createTheme`. */
  muiLocale: MuiLocaleBundle;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function isSupported(value: string | null | undefined): value is SupportedLanguage {
  return !!value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

function normalize(value: string | null | undefined): SupportedLanguage {
  // Map BCP-47 region tags (es-MX) down to the base catalog (es) for now.
  const base = value?.split('-')[0];
  return isSupported(base) ? base : FALLBACK_LANGUAGE;
}

const DATE_FNS_LOCALES: Record<SupportedLanguage, DateFnsLocale> = {
  en: dateFnsEnUS,
  es: dateFnsEs,
};

const MUI_LOCALES: Record<SupportedLanguage, MuiLocaleBundle> = {
  en: muiEnUS,
  es: muiEsES,
};

// ----------------------------------------------------------------------

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => normalize(i18n.language));

  const dbLanguage = useAuthStore((state) => state.dbUser?.language);
  const updateProfile = useAuthControllerUpdateProfile();

  // Keep React state, <html lang>, and i18next in sync on any language change.
  useEffect(() => {
    const handleChange = (lng: string) => {
      const next = normalize(lng);
      setLanguageState(next);
      document.documentElement.lang = next;
    };
    document.documentElement.lang = normalize(i18n.language);
    i18n.on('languageChanged', handleChange);
    return () => {
      i18n.off('languageChanged', handleChange);
    };
  }, []);

  // Once the authed user is known, their persisted DB preference wins over
  // the pre-auth detector result.
  useEffect(() => {
    const next = normalize(dbLanguage);
    if (dbLanguage && next !== normalize(i18n.language)) {
      void i18n.changeLanguage(next);
    }
  }, [dbLanguage]);

  const setLanguage = useCallback(
    (next: SupportedLanguage) => {
      void i18n.changeLanguage(next);
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);

      const { dbUser, setAuthenticated } = useAuthStore.getState();
      if (dbUser) {
        // Optimistically reflect the choice in the cached user, then persist.
        setAuthenticated({ ...dbUser, language: next });
        updateProfile.mutate({ data: { language: next } });
      }
    },
    [updateProfile]
  );

  const value = useMemo<LocaleContextValue>(
    () => ({
      language,
      setLanguage,
      dateFnsLocale: DATE_FNS_LOCALES[language],
      muiLocale: MUI_LOCALES[language],
    }),
    [language, setLanguage]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
